import { useState, useEffect } from 'react';
import axios from 'axios';

/**
 * Le référentiel des leviers de ciblage, reçu du serveur.
 *
 * ## ⚠️ Pourquoi aucune table n'est écrite ici
 *
 * Les libellés, les aides, les indices de panneau, les familles et la correspondance avec les
 * `subtype` d'audience Meta vivent dans `backend/src/constants/leviersCiblage.js`, **et nulle
 * part ailleurs**. Deux raisons, dans cet ordre :
 *
 * 1. `react-scripts` interdit d'importer hors de `frontend/src` (`ModuleScopePlugin`), et les
 *    deux moitiés sont déployées séparément — Vercel et Railway. Un vrai module partagé est
 *    donc impossible sans toucher au build.
 * 2. Même s'il était possible, une **copie** divergerait au premier libellé corrigé, et la
 *    divergence serait muette : l'écran nommerait un levier autrement que l'export, le prompt
 *    d'analyse et l'outil MCP. C'est le défaut que les deux registres `clientTools.js` doivent
 *    tenir par un script, et qu'on évite ici en n'ayant qu'un exemplaire.
 *
 * Le précédent est `OngletMontantsParCible.jsx`, qui reçoit son catalogue de destinations du
 * serveur pour la même raison.
 *
 * ⚠️ **Et il n'y a aucun libellé de repli.** Si le référentiel ne se charge pas, l'écran le dit
 * et n'affiche rien : un libellé inventé se lirait comme une information vérifiée — la leçon
 * des sept noms inventés du repli de `ga4FieldValidator`.
 */

/**
 * Mémorisé à l'échelle du module : le référentiel ne change pas d'un concurrent à l'autre, et
 * le recharger à chaque ouverture d'onglet coûterait une requête pour rien.
 */
let cache = null;

export function useLeviersCiblage(token) {
  const [referentiel, setReferentiel] = useState(cache);
  const [erreur, setErreur] = useState(null);
  const [chargement, setChargement] = useState(!cache);

  useEffect(() => {
    if (cache || !token) return;
    let vivant = true;

    (async () => {
      try {
        const { data } = await axios.get('/api/competitors/ciblage/leviers', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!vivant) return;
        cache = data.data;
        setReferentiel(cache);
      } catch (e) {
        if (vivant) {
          setErreur("Le référentiel des leviers n'a pas pu être chargé. L'écran ne peut pas "
            + "nommer les signaux sans lui.");
        }
      } finally {
        if (vivant) setChargement(false);
      }
    })();

    return () => { vivant = false; };
  }, [token]);

  /**
   * Le levier d'une clé.
   *
   * ⚠️ Une clé absente du référentiel est rendue **telle quelle**, jamais masquée : une ligne
   * qui disparaît se lit comme une absence de données, tandis qu'une ligne nommée
   * « RETARGETING_XYZ » envoie corriger le registre. Même discipline que `levierDe` côté
   * serveur.
   */
  const levier = (cle) => (referentiel?.leviers || []).find(l => l.cle === cle)
    || { cle, libelle: cle, famille: 'aucun_signal', aide: '', indices: [], subtypesMeta: [] };

  const libelleDe = (liste, cle) => (referentiel?.[liste] || []).find(e => e.cle === cle)?.libelle || cle;

  return {
    referentiel,
    chargement,
    erreur,
    levier,
    leviers: referentiel?.leviers || [],
    familles: referentiel?.familles || [],
    sources: referentiel?.sources || [],
    surfaces: referentiel?.surfaces || [],
    gestes: referentiel?.gestes || [],
    seuils: referentiel?.seuils || null,
    libelleSource: (cle) => libelleDe('sources', cle),
    libelleSurface: (cle) => libelleDe('surfaces', cle),
    libelleGeste: (cle) => libelleDe('gestes', cle)
  };
}

/**
 * Le délai, mis en mots.
 *
 * ⚠️ **Jamais « 0 » quand il est indisponible.** Le serveur rend un `indisponible` nommé plutôt
 * qu'un zéro, précisément pour que l'écran ne puisse pas écrire « arrivée dans l'heure » sur une
 * absence de mesure. Cette fonction est le dernier endroit où la distinction pourrait se perdre.
 *
 * Et le délai **nomme toujours son origine** : « 6 h après l'ajout au panier » se lit, « 6 h »
 * tout seul ne veut rien dire.
 */
export function delaiEnMots(delai, libelleGeste) {
  if (!delai || delai.indisponible) return null;

  const { heures, jours } = delai;
  let duree;
  if (heures < 1) duree = "moins d'une heure";
  else if (heures < 48) duree = `${heures} h`;
  else duree = `${jours} jours`;

  return `${duree} après ${libelleGeste(delai.geste).toLowerCase()}`;
}
